import csvDownload from 'json-to-csv-export';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getSpvTaxXML } from 'src/api/stoApi';
import { ConfirmDownload } from 'src/components/dialogs/ConfirmDownload';
import { ETaxType } from 'src/models/enum';
import { ITaxReport, ITaxReportDate, SPV } from 'src/models/index';
import { determineWhatTaxType } from 'src/util/taxes';
import { awaitUtil, downloadXML } from 'src/util/utils';
import CodeIcon from '@mui/icons-material/Code';
import DownloadIcon from '@mui/icons-material/Download';
import { Chip } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

function createData(date: ITaxReportDate, grossIncome: string, taxType: string, perAsset: ITaxReport['perAsset']) {
  // We add +1 to the month because the month is 0-indexed
  return { date: `${date.month + 1}.${date.year}`, grossIncome: grossIncome, taxType, month: date.month, year: date.year, perAsset };
}

type TTableRow = {
  date: string;
  grossIncome: string;
  taxType: string;
  month: number;
  year: number;
  perAsset: ITaxReport['perAsset'];
};

const selectTaxType = (taxType: ETaxType, row: ITaxReport) => {
  switch (taxType) {
    case ETaxType.KESt:
      return row.totalKESt;
    default:
      return '0.00';
  }
};

export const TaxReportTableAustria = ({ spv, taxReport }: { spv: SPV; taxReport: ITaxReport[] }) => {
  const { t } = useTranslation();
  const [kestXmlDialog, setKestXmlDialog] = useState(false);
  const [kestXmlMonth, setKestXmlMonth] = useState(0);
  const [kestXmlYear, setKestXmlYear] = useState(0);
  // const [page, setPage] = React.useState(0);
  // const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const taxType = determineWhatTaxType(spv);
  //   const [rows, setRows] = useState<TableRow[]>([]);
  const transactionData = taxReport;

  const rows = transactionData.map((row) => createData(row.date, row.grossIncome, selectTaxType(taxType, row), row.perAsset));

  const openKestXmlDialog = (month: number, year: number) => {
    setKestXmlMonth(month);
    setKestXmlYear(year);
    setKestXmlDialog(true);
  };

  const downloadReport = (row: TTableRow) => {
    const taxArray = row.perAsset.map((taxes) => {
      return {
        date: row.date,
        asset: taxes.assetId,
        grossIncome: taxes.grossIncome,
        tax: taxes.KESt,
      };
    });
    // const taxes = [{ date: row.date, grossIncome: row.grossIncome, tax: row.taxType }];

    const dataToConvert = {
      data: taxArray,
      filename: 'issuer_tax_report',
      delimiter: ';',
      headers: [
        t('issuer.tax.table.monthYear'),
        t('issuer.tax.table.assetId'),
        t('issuer.tax.table.grossCapIncome'),
        t('issuer.tax.table.tax'),
      ], // headers: ['Date', Asset Id,  'Gross sum of payout', 'Taxes',],
    };
    csvDownload(dataToConvert);
  };
  const fetchTaxXML = async () => {
    const yearString = kestXmlYear.toString();
    const monthString = kestXmlMonth.toString();
    try {
      const [data, error] = await awaitUtil(getSpvTaxXML(spv.id, yearString, monthString));
      if (error) return;
      if (!data) return;
      downloadXML(data.data.data, `Kapitalertragsteuer_Anmeldung_${kestXmlYear}_${kestXmlMonth + 1}.xml`);
    } catch (error) {}
  };

  // Pagination deactivated for now. Will activate once backend supports pagination
  // const handleChangePage = (event: unknown, newPage: number) => {
  //   setPage(newPage);
  // };
  // Pagination deactivated for now. Will activate once backend supports pagination
  // const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
  //   setRowsPerPage(Number(event.target.value));
  //   setPage(0);
  // };

  return (
    <>
      <TableContainer>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell>{t('issuer.tax.table.monthYear')}</TableCell>
              <TableCell>{t('issuer.tax.table.grossCapIncome')}</TableCell>
              <TableCell>
                {t('issuer.tax.table.tax')} ({taxType})
              </TableCell>
              <TableCell align="center">{t('issuer.tax.table.report')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row: TTableRow) => (
              <TableRow key={row.date} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                <TableCell component="th" scope="row">
                  {row.date}
                </TableCell>
                <TableCell align="left">{Number(row.grossIncome).toFixed(2)} €</TableCell>
                <TableCell align="left">{Number(row.taxType).toFixed(2)} €</TableCell>
                <TableCell align="right">
                  <Chip sx={{ mr: 2 }} icon={<DownloadIcon />} onClick={() => downloadReport(row)} label="Download CSV" />
                  <Chip icon={<CodeIcon />} onClick={() => openKestXmlDialog(row.month, row.year)} label="Download XML" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {/* 
      Pagination deactivated for now. Will activate once backend supports pagination
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={-1}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        nextIconButtonProps={
          rows.length < rowsPerPage
            ? {
                disabled: true,
              }
            : undefined
        }
      /> */}
      <ConfirmDownload
        open={kestXmlDialog}
        setClose={setKestXmlDialog}
        title={t('issuer.tax.legalDialog.kest.title')}
        disclaimer={t('issuer.tax.legalDialog.kest.body')}
        downloadButton={fetchTaxXML}
        buttonText={t('issuer.tax.legalDialog.kest.button')}
      />
    </>
  );
};
